/**
 * Ontology Graph View - Cytoscape.js visualization for private ontologies
 *
 * Features:
 * - Cola force-directed layout (Neo4j-like physics) with cose fallback
 * - Dynamic node sizing based on connections
 * - Spotlight hover effect (dim non-neighbors)
 * - Interactive pan/zoom/select
 * - Glass-morphism UI controls
 *
 * @version 2.0.0
 * @date 2025-12-13
 */

import React, { useEffect, useRef, useMemo, useState } from 'react';
import cytoscape, { Core, ElementDefinition } from 'cytoscape';

// Try to register cola extension for better layouts (optional - falls back to cose)
let colaAvailable = false;
try {
  const cola = require('cytoscape-cola');
  cytoscape.use(cola);
  colaAvailable = true;
} catch {
  // cytoscape-cola not installed - using cose layout (works fine)
}

// =============================================================================
// TYPES
// =============================================================================

export interface OntologyClass {
  uri: string;
  local_name: string;
  label?: string;
  comment?: string;
  sub_classes?: string[];
  parent_classes?: string[];
}

export interface OntologyProperty {
  uri: string;
  local_name: string;
  label?: string;
  comment?: string;
  type: 'object' | 'datatype' | 'annotation';
  domain?: string[];
  range?: string[];
  pattern?: string;
}

export interface OntologyIndividual {
  uri: string;
  local_name: string;
  label?: string;
  types: string[];
  properties: Record<string, string[]>;
}

export interface ParsedContent {
  prefixes?: Record<string, string>;
  classes: OntologyClass[];
  properties: OntologyProperty[];
  individuals: OntologyIndividual[];
  raw_triples_count?: number;
}

interface OntologyGraphViewProps {
  parsedContent: ParsedContent;
  onNodeSelect?: (uri: string) => void;
  theme?: 'dark' | 'light';
}

// =============================================================================
// STYLES (Zero-Dependency CSS)
// =============================================================================

const THEME = {
  bg: '#111827',
  text: '#ffffff',
  accent: '#3b82f6',
  panelBg: 'rgba(31, 41, 55, 0.85)',
  border: 'rgba(75, 85, 99, 0.5)',
};

const CONTAINER_STYLE: React.CSSProperties = {
  position: 'relative',
  width: '100%',
  height: '100%',
  backgroundColor: THEME.bg,
  overflow: 'hidden',
  fontFamily: 'Inter, system-ui, sans-serif',
};

const CANVAS_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1,
};

const CONTROL_PANEL_STYLE: React.CSSProperties = {
  position: 'absolute',
  bottom: '20px',
  right: '20px',
  display: 'flex',
  gap: '8px',
  zIndex: 10,
};

const BUTTON_STYLE: React.CSSProperties = {
  background: '#1f2937',
  color: 'white',
  border: `1px solid ${THEME.border}`,
  padding: '8px 16px',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '12px',
  fontWeight: 600,
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.5)',
  transition: 'all 0.2s',
};

const LEGEND_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: '20px',
  right: '20px',
  background: THEME.panelBg,
  backdropFilter: 'blur(4px)',
  padding: '12px',
  borderRadius: '8px',
  border: `1px solid ${THEME.border}`,
  color: 'white',
  fontSize: '12px',
  zIndex: 10,
  pointerEvents: 'none',
  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5)',
};

// =============================================================================
// COMPONENT
// =============================================================================

export const OntologyGraphView: React.FC<OntologyGraphViewProps> = ({
  parsedContent,
  onNodeSelect,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const cyRef = useRef<Core | null>(null);
  const [isColaAvailable] = useState(colaAvailable);

  // --- Data Transformation (Memoized) ---
  const elements = useMemo(() => {
    const els: ElementDefinition[] = [];
    const nodeIds = new Set<string>();

    const addNode = (id: string, label: string, type: 'class' | 'individual', weight: number) => {
      if (!nodeIds.has(id)) {
        nodeIds.add(id);
        els.push({
          data: { id, label, type, weight, uri: id },
          classes: type,
        });
      }
    };

    // Process Classes
    parsedContent.classes.forEach((cls) => {
      const weight = 10 + ((cls.sub_classes?.length || 0) * 2);
      addNode(cls.uri, cls.label || cls.local_name, 'class', weight);
    });

    // Process Individuals
    parsedContent.individuals.forEach((ind) => {
      addNode(ind.uri, ind.label || ind.local_name, 'individual', 5);
    });

    // Process Edges (Hierarchy)
    parsedContent.classes.forEach((cls) => {
      cls.parent_classes?.forEach((parentUri) => {
        if (nodeIds.has(parentUri) && nodeIds.has(cls.uri)) {
          els.push({
            data: { source: cls.uri, target: parentUri, label: 'subClassOf' },
            classes: 'hierarchy',
          });
        }
      });
    });

    // Process Edges (Object Properties)
    parsedContent.properties.forEach((prop) => {
      if (prop.type === 'object' && prop.domain && prop.range) {
        prop.domain.forEach((d) => {
          prop.range!.forEach((r) => {
            const s = parsedContent.classes.find((c) => c.uri === d || c.local_name === d)?.uri;
            const t = parsedContent.classes.find((c) => c.uri === r || c.local_name === r)?.uri;
            if (s && t && nodeIds.has(s) && nodeIds.has(t)) {
              els.push({
                data: { source: s, target: t, label: prop.label || prop.local_name },
                classes: 'property',
              });
            }
          });
        });
      }
    });

    // Process Edges (Instances)
    parsedContent.individuals.forEach((ind) => {
      ind.types.forEach((t) => {
        const target = parsedContent.classes.find((c) => c.uri === t || c.local_name === t)?.uri;
        if (target && nodeIds.has(ind.uri) && nodeIds.has(target)) {
          els.push({
            data: { source: ind.uri, target, label: 'instanceOf' },
            classes: 'instance',
          });
        }
      });
    });

    return els;
  }, [parsedContent]);

  // --- Cytoscape Initialization ---
  useEffect(() => {
    if (!containerRef.current) return;

    const cy = cytoscape({
      container: containerRef.current,
      elements,
      minZoom: 0.1,
      maxZoom: 4,
      wheelSensitivity: 0.2,

      style: [
        // CORE NODE STYLE
        {
          selector: 'node',
          style: {
            label: 'data(label)',
            color: '#ffffff',
            'font-family': 'Inter, sans-serif',
            'font-size': '10px',
            'font-weight': 600,
            'text-valign': 'bottom',
            'text-margin-y': 5,
            'text-outline-color': '#000',
            'text-outline-width': 2,
            'z-index': 10,
          },
        },
        // CLASS NODES
        {
          selector: 'node.class',
          style: {
            'background-color': '#3b82f6',
            width: (ele: cytoscape.NodeSingular) => Math.min(70, Math.max(20, ele.data('weight') || 20)),
            height: (ele: cytoscape.NodeSingular) => Math.min(70, Math.max(20, ele.data('weight') || 20)),
            shape: 'round-rectangle',
            'shadow-blur': 15,
            'shadow-color': '#3b82f6',
            'shadow-opacity': 0.4,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Cytoscape shadow styles not in types
          } as any,
        },
        // INDIVIDUAL NODES
        {
          selector: 'node.individual',
          style: {
            'background-color': '#10b981',
            width: 15,
            height: 15,
            shape: 'ellipse',
            label: '',
          },
        },
        // EDGES
        {
          selector: 'edge',
          style: {
            width: 2,
            'curve-style': 'bezier',
            'target-arrow-shape': 'triangle',
            'line-color': '#4b5563',
            'target-arrow-color': '#4b5563',
            opacity: 0.5,
          },
        },
        // HIERARCHY EDGES
        {
          selector: 'edge.hierarchy',
          style: {
            'line-color': '#60a5fa',
            'target-arrow-color': '#60a5fa',
            width: 3,
            opacity: 0.8,
          },
        },
        // PROPERTY EDGES
        {
          selector: 'edge.property',
          style: {
            'line-color': '#818cf8',
            'target-arrow-color': '#818cf8',
            'line-style': 'dashed',
            opacity: 0.6,
          },
        },
        // INSTANCE EDGES
        {
          selector: 'edge.instance',
          style: {
            'line-color': '#10b981',
            'target-arrow-color': '#10b981',
            width: 1,
            opacity: 0.4,
          },
        },
        // SELECTED
        {
          selector: ':selected',
          style: {
            'background-color': '#f59e0b',
            'line-color': '#f59e0b',
            'target-arrow-color': '#f59e0b',
            'source-arrow-color': '#f59e0b',
            opacity: 1,
            'z-index': 999,
            'border-width': 2,
            'border-color': '#fff',
          },
        },
        // DIMMED (for spotlight effect)
        {
          selector: '.dimmed',
          style: { opacity: 0.1, 'z-index': 0 },
        },
        // HIGHLIGHTED (for spotlight effect)
        {
          selector: '.highlighted',
          style: { opacity: 1, 'z-index': 999, 'text-outline-color': '#f59e0b' },
        },
      ],

      layout: {
        name: isColaAvailable ? 'cola' : 'cose',
        animate: true,
        infinite: false,
        fit: false, // Don't auto-shrink to fit screen
        center: true, // Center the graph so it's not off-screen
        padding: 50,

        // Dynamic node spacing - class nodes get more buffer
        nodeSpacing: (node: cytoscape.NodeSingular) => {
          return node.data('type') === 'class' ? 150 : 60;
        },

        // Dynamic edge length - structural edges longer to separate clusters
        edgeLength: (edge: cytoscape.EdgeSingular) => {
          const label = edge.data('label');
          if (label === 'subClassOf') return 450; // Main backbone = Long
          if (label === 'instanceOf') return 150; // Leaves = Short
          return 300; // Properties = Medium
        },

        // Collision avoidance - prevents text overlap
        nodeDimensionsIncludeLabels: true,

        // Low gravity - allows graph to spread outward
        gravity: 0.05,

        // Simulation settings
        refresh: 1,
        maxSimulationTime: 4000,
        randomize: false,
        ungrabifyWhileSimulating: true,

        // Fallback settings for cose layout
        ...(isColaAvailable
          ? {}
          : {
              nodeRepulsion: () => 50000,
              idealEdgeLength: () => 300,
              numIter: 2000,
            }),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Cola layout options not in types
      } as any,
    });

    // Set initial zoom level so nodes aren't overwhelming
    cy.zoom(0.8);
    cy.center();

    cyRef.current = cy;

    // --- Hover Spotlight Effect ---
    cy.on('mouseover', 'node', (e) => {
      const node = e.target;
      const neighborhood = node.neighborhood().add(node);
      cy.elements().addClass('dimmed');
      neighborhood.removeClass('dimmed').addClass('highlighted');
    });

    cy.on('mouseout', 'node', () => {
      cy.elements().removeClass('dimmed').removeClass('highlighted');
    });

    // --- Click Selection ---
    cy.on('tap', 'node', (evt) => {
      const node = evt.target;
      onNodeSelect?.(node.data('uri'));
    });

    return () => {
      cy.destroy();
    };
  }, [elements, isColaAvailable, onNodeSelect]);

  // --- Controls ---
  const handleFit = () => cyRef.current?.fit(undefined, 50);

  const handleReset = () => {
    cyRef.current?.zoom(1);
    cyRef.current?.center();
  };

  const handleLayout = () => {
    cyRef.current
      ?.layout({
        name: isColaAvailable ? 'cola' : 'cose',
        animate: true,
        fit: false,
        center: true,
        maxSimulationTime: 3000,
        randomize: true,
        nodeSpacing: (node: cytoscape.NodeSingular) => (node.data('type') === 'class' ? 150 : 60),
        edgeLength: (edge: cytoscape.EdgeSingular) => {
          const label = edge.data('label');
          if (label === 'subClassOf') return 450;
          if (label === 'instanceOf') return 150;
          return 300;
        },
        nodeDimensionsIncludeLabels: true,
        gravity: 0.05,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Cola layout options not in types
      } as any)
      .run();
  };

  // --- Counts for legend ---
  const classCount = parsedContent.classes.length;
  const individualCount = parsedContent.individuals.length;

  return (
    <div style={CONTAINER_STYLE}>
      <div ref={containerRef} style={CANVAS_STYLE} />

      {/* Legend */}
      <div style={LEGEND_STYLE}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <div style={{ width: '12px', height: '12px', background: '#3b82f6', borderRadius: '3px' }} />
          <span>Classes ({classCount})</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <div style={{ width: '12px', height: '12px', background: '#10b981', borderRadius: '50%' }} />
          <span>Individuals ({individualCount})</span>
        </div>
        <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '8px' }}>
          Hover to spotlight • Click to select
        </div>
      </div>

      {/* Controls */}
      <div style={CONTROL_PANEL_STYLE}>
        <button
          style={BUTTON_STYLE}
          onClick={handleFit}
          onMouseOver={(e) => (e.currentTarget.style.background = '#374151')}
          onMouseOut={(e) => (e.currentTarget.style.background = '#1f2937')}
          title="Fit entire graph to view"
        >
          Fit View
        </button>
        <button
          style={BUTTON_STYLE}
          onClick={handleReset}
          onMouseOver={(e) => (e.currentTarget.style.background = '#374151')}
          onMouseOut={(e) => (e.currentTarget.style.background = '#1f2937')}
          title="Reset to 100% zoom"
        >
          1:1
        </button>
        <button
          style={BUTTON_STYLE}
          onClick={handleLayout}
          onMouseOver={(e) => (e.currentTarget.style.background = '#374151')}
          onMouseOut={(e) => (e.currentTarget.style.background = '#1f2937')}
          title="Re-organize layout"
        >
          Organize
        </button>
      </div>
    </div>
  );
};

export default OntologyGraphView;
